// src/components/activities/ActivityDetailsDrawer.tsx
import React, { useEffect, useState } from 'react';
import {
  Drawer, Tag, Typography, Space, message, Skeleton, Avatar,
  Divider, Button, Badge
} from 'antd';
import {
  CalendarOutlined, UserOutlined, EyeOutlined, EyeInvisibleOutlined,
  FileTextOutlined, TagOutlined, MailOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getActivity, type ActivityDTO } from '../../services/activities';
import FileManager from '../files/FileManager';
import CreateActivityDrawer from './CreateActivityDrawer';
import { renderAntdIcon } from '../../utils/antdIcon';

const { Paragraph, Text } = Typography;

type Props = {
  open: boolean;
  activityId?: number | null;
  onClose: () => void;
  onRefresh?: () => void;
  canEdit?: boolean;
};

const ActivityDetailsDrawer: React.FC<Props> = ({
  open,
  activityId,
  onClose,
  onRefresh,
  canEdit = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ActivityDTO | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const allowEdit = canEdit && !!activityId;

  const fetch = async () => {
    if (!activityId) return;
    setLoading(true);
    try {
      const a = await getActivity(activityId);
      setData(a);
    } catch (e: any) {
      message.error(e?.message || 'Impossibile caricare l\'attività');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && activityId) fetch();
    if (!open) setData(null);
    if (!open) setEditOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activityId]);

  useEffect(() => {
    if (!allowEdit) setEditOpen(false);
  }, [allowEdit]);

  const iconEl = renderAntdIcon(data?.activityType?.icon);
  const label = data?.activityType?.label || data?.activityType?.code || '—';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar style={{ background: '#f0f5ff', color: '#1677ff' }} icon={iconEl} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, margin: 0, color: '#262626' }}>
              #{data?.id} — {data?.name || label}
            </div>
            <Text type="secondary">{label}</Text>
          </div>
        </div>
      }
      width={680}
      destroyOnClose
      extra={
        <Space size={8}>
          {allowEdit && (
            <Button
              type="primary"
              ghost
              onClick={() => setEditOpen(true)}
            >
              Modifica
            </Button>
          )}
          <Button onClick={onClose} style={{ borderRadius: 6, height: 36 }}>
            Chiudi
          </Button>
        </Space>
      }
    >
      <Skeleton active loading={loading} paragraph={{ rows: 6 }}>
        {/* Informazioni */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <FileTextOutlined />
            <span style={{ fontWeight: 600, fontSize: 15 }}>Informazioni</span>
          </div>

          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              background: '#fafafa',
              borderRadius: 8,
              overflow: 'hidden',
              border: '1px solid #f0f0f0'
            }}
          >
            <tbody>
              <tr>
                <td style={{
                  padding: '12px 16px',
                  fontWeight: 600,
                  color: '#595959',
                  fontSize: 14,
                  width: 140,
                  borderBottom: '1px solid #f0f0f0',
                  background: '#f5f5f5'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CalendarOutlined />
                    Data e Ora
                  </div>
                </td>
                <td style={{
                  padding: '12px 16px',
                  color: '#262626',
                  fontSize: 14,
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <div>
                    <Text style={{ fontSize: 15, fontWeight: 600 }}>
                      {data?.occurredAt ? dayjs(data.occurredAt).format('DD/MM/YYYY HH:mm') : '—'}
                    </Text>
                    {data?.occurredAt && (
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {dayjs(data.occurredAt).fromNow()}
                        </Text>
                      </div>
                    )}
                  </div>
                </td>
              </tr>

              <tr>
                <td style={{
                  padding: '12px 16px',
                  fontWeight: 600,
                  color: '#595959',
                  fontSize: 14,
                  borderBottom: '1px solid #f0f0f0',
                  background: '#f5f5f5'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {data?.visibility ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    Visibilità
                  </div>
                </td>
                <td style={{
                  padding: '12px 16px',
                  color: '#262626',
                  fontSize: 14,
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <Badge
                    status={data?.visibility ? 'success' : 'default'}
                    text={<Text style={{ fontSize: 13, fontWeight: 500 }}>{data?.visibility ? 'Visibile al cliente' : 'Privata'}</Text>}
                  />
                </td>
              </tr>

              <tr>
                <td style={{
                  padding: '12px 16px',
                  fontWeight: 600,
                  color: '#595959',
                  fontSize: 14,
                  borderBottom: '1px solid #f0f0f0',
                  background: '#f5f5f5'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <TagOutlined />
                    Tipo
                  </div>
                </td>
                <td style={{
                  padding: '12px 16px',
                  color: '#262626',
                  fontSize: 14,
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <Tag
                    icon={iconEl as any}
                    style={{
                      fontWeight: 500,
                      padding: '4px 12px',
                      borderRadius: 6,
                      fontSize: 13
                    }}
                  >
                    {label}
                  </Tag>
                </td>
              </tr>

              <tr>
                <td style={{
                  padding: '12px 16px',
                  fontWeight: 600,
                  color: '#595959',
                  fontSize: 14,
                  borderBottom: '1px solid #f0f0f0',
                  background: '#f5f5f5'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <UserOutlined />
                    Cliente
                  </div>
                </td>
                <td style={{
                  padding: '12px 16px',
                  color: '#262626',
                  fontSize: 14,
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <div style={{
                    padding: '6px 12px',
                    background: '#f0f9ff',
                    borderRadius: 6,
                    border: '1px solid #e6f4ff',
                    display: 'inline-block'
                  }}>
                    <Text style={{ fontWeight: 500 }}>
                      {data?.customerName || '—'}
                    </Text>
                  </div>
                </td>
              </tr>

              {data?.description && (
                <tr>
                  <td style={{
                    padding: '12px 16px',
                    fontWeight: 600,
                    color: '#595959',
                    fontSize: 14,
                    borderBottom: '1px solid #f0f0f0',
                    background: '#f5f5f5'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FileTextOutlined />
                      Descrizione
                    </div>
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    color: '#262626',
                    fontSize: 14,
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    <Paragraph style={{ margin: 0 }}>{data.description}</Paragraph>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Allegati */}
        {data?.attachments?.length ? (
          <>
            <Divider />
            <FileManager entity='activity' entityId={data.id!} />
          </>
        ) : null}
      </Skeleton>

      {allowEdit && (
        <CreateActivityDrawer
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onUpdated={() => {
            setEditOpen(false);
            if (activityId) fetch();
            onRefresh?.();
          }}
          mode="edit"
          activityId={activityId ?? undefined}
          lockType
        />
      )}
    </Drawer>
  );
};

export default ActivityDetailsDrawer;
